import React, { useState } from "react";
import { Eye, EyeOff, Loader } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import AuthLayout from "./AuthLayout";
import BackArrowIcon from "../../assets/auth/back-arrow.svg";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { signup } from "../../store/authSlice";
import UserSignInIcon from "../../assets/auth/user-sign-in.svg";

interface FormData {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
}

export function SignupUserForm(): JSX.Element {
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [touched, setTouched] = useState<{ [key: string]: boolean }>({});
  const [showPassword, setShowPassword] = useState<boolean>(true);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  const dispatch = useAppDispatch();
  const { fetchLoading, fetchError } = useAppSelector((state) => state.auth);
  const navigate = useNavigate();

  const handleFocus = (field: string) => {
    setTouched((prev) => {
      const newTouched = { ...prev };
      delete newTouched[field];
      return newTouched;
    });
  };

  const getInputClassName = (field: keyof FormData) => {
    const hasError =
      touched[field] && (formErrors[field] || !formData[field].trim());
    return `w-full px-4 py-3 rounded-md outline-none nunito-medium ${
      hasError
        ? "border border-red-500 text-red-500 placeholder-red-400"
        : "border border-[#D9D9D9]"
    }`;
  };

  const validatePassword = (password: string): boolean => {
    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,}$/;
    return passwordRegex.test(password);
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const validateField = (name: string, value: string) => {
    switch (name) {
      case "email":
        if (!value) return "*Required";
        if (!validateEmail(value)) {
          return "Please enter a valid email address";
        }
        return "";
      case "password":
        if (!value) return "*Required";
        if (!validatePassword(value)) {
          return "Password must contain at least 8 characters, uppercase, lowercase, number and special character";
        }
        return "";
      case "confirmPassword":
        if (!value) return "*Required";
        if (value !== formData.password) {
          return "Passwords do not match";
        }
        return "";
      default:
        return value.trim() === "" ? "*Required" : "";
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Validate email in real-time after first touch
    if (touched[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: validateField(name, value),
      }));
    }

    // Special handling for email field
    if (name === "email" && touched.email) {
      const emailError = validateField("email", value);
      setFormErrors((prev) => ({
        ...prev,
        email: emailError,
      }));
    }

    // Validate confirmPassword when password changes
    if (name === "password" && formData.confirmPassword) {
      setFormErrors((prev) => ({
        ...prev,
        confirmPassword:
          formData.confirmPassword !== value ? "*Passwords do not match" : "",
      }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    setFormErrors((prev) => ({
      ...prev,
      [name]: validateField(name, value),
    }));
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();

    // Mark all fields as touched on submit
    const allTouched = {
      name: true,
      email: true,
      password: true,
      confirmPassword: true,
    };
    setTouched(allTouched);

    // Validate all fields
    const newErrors: FormErrors = {};
    Object.keys(formData).forEach((key) => {
      const fieldKey = key as keyof FormData;
      const value = formData[fieldKey];
      const validationError = validateField(key, value);
      if (validationError) {
        newErrors[fieldKey] = validationError;
      }
    });

    setFormErrors(newErrors);

    // If there are any errors, stop form submission
    if (Object.keys(newErrors).length > 0) {
      return;
    }

    // Continue with form submission if no errors
    setError(null);

    try {
      const resultAction = await dispatch(
        signup({
          name: formData.name,
          email: formData.email,
          password: formData.password,
          role: "Customer",
        })
      ).unwrap();

      if (resultAction.success) {
        navigate("/signup/2FA", {
          state: { email: formData.email, password: formData.password },
        });
      } else {
        setError("Failed to create account. Please try again.");
      }
    } catch (error: any) {
      console.error("Failed to sign up:", error);
      if (error.includes("auth/email-already-in-use")) {
        setError(
          "Email already in use. Please sign in or use a different email."
        );
      } else {
        setError(error || "An unexpected error occurred. Please try again.");
      }
    }
  };

  return (
    <AuthLayout>
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-row w-full shadow-lg rounded-lg overflow-hidden h-[100vh]">
          {/* Left Column: Signup Form */}
          <div className="flex-1 flex flex-col justify-center p-8 bg-white space-y-6 items-center">
            <div className="w-full space-y-8">
              <div>
                <div className="flex justify-center items-center gap-5">
                  <button onClick={() => navigate(-1)}>
                    <img
                      src={BackArrowIcon}
                      alt="back arrow"
                      className="h-14 text-[#919191]"
                    />
                  </button>
                  <h2 className="text-center text-title nunito-extrabold text-[#0A0A0A]">
                    Create your User account
                  </h2>
                </div>

                <p className="mt-2 text-center nunito-regular text-[#696969]">
                  or{" "}
                  <Link
                    to="/signin"
                    className="nunito-semibold text-[#F15D18] hover:text-brand-primary"
                  >
                    sign in
                  </Link>{" "}
                  to your account
                </p>
              </div>

              {(error || fetchError) && (
                <div className="mt-4 rounded-md bg-red-50 p-4 text-center">
                  <p className="text-sm text-red-700">{error || fetchError}</p>
                </div>
              )}

              <div className="flex items-center justify-center mt-10">
                <form className="w-2/4" onSubmit={handleSubmit}>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm nunito-semibold text-[#1D1D1D] mb-1">
                        Full Name
                      </label>
                      <input
                        name="name"
                        type="text"
                        value={formData.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        onFocus={() => handleFocus("name")}
                        className={getInputClassName("name")}
                        placeholder="Enter your full name"
                      />
                      {touched.name &&
                        (!formData.name.trim() || formErrors.name) && (
                          <p className="text-[10px] nunito-medium text-[#D5332A] mt-1">
                            {formErrors.name || "*Required"}
                          </p>
                        )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#1D1D1D] mb-1">
                        Email
                      </label>
                      <input
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        onFocus={() => handleFocus("email")}
                        className={getInputClassName("email")}
                        placeholder="Enter your email address"
                      />
                      {touched.email &&
                        (!formData.email.trim() || formErrors.email) && (
                          <p className="text-[10px] nunito-medium text-[#D5332A] mt-1">
                            {formErrors.email || "*Required"}
                          </p>
                        )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#1D1D1D] mb-1">
                        Password
                      </label>
                      <div className="relative">
                        <input
                          name="password"
                          type={!showPassword ? "text" : "password"}
                          value={formData.password}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          onFocus={() => handleFocus("password")}
                          className={getInputClassName("password")}
                          placeholder="Password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                        >
                          {showPassword ? (
                            <EyeOff className="h-5 w-5 text-[#919191]" />
                          ) : (
                            <Eye className="h-5 w-5 text-[#919191]" />
                          )}
                        </button>
                      </div>
                      {touched.password && formErrors.password && (
                        <p className="text-[10px] nunito-medium text-[#D5332A] mt-1">
                          {formErrors.password}
                        </p>
                      )}
                      <p className="text-xs text-[#919191] mt-2">
                        Password must be minimum 8 characters, with uppercase,
                        lowercase, a number (0-9), and a special character (!,
                        @, #, $, %, ^, &, *)
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#1D1D1D] mb-1">
                        Confirm Password
                      </label>
                      <div className="relative">
                        <input
                          name="confirmPassword"
                          type={!showConfirmPassword ? "text" : "password"}
                          value={formData.confirmPassword}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          onFocus={() => handleFocus("confirmPassword")}
                          className={getInputClassName("confirmPassword")}
                          placeholder="Confirm Password"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="h-5 w-5 text-[#919191]" />
                          ) : (
                            <Eye className="h-5 w-5 text-[#919191]" />
                          )}
                        </button>
                      </div>
                      {touched.confirmPassword &&
                        formErrors.confirmPassword && (
                          <p className="text-[10px] nunito-medium text-[#D5332A] mt-1">
                            {formErrors.confirmPassword}
                          </p>
                        )}
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={fetchLoading}
                    className="mt-10 w-full flex items-center justify-center px-4 py-3 bg-[#F15D18] text-[#FFFFFF] nunito-bold rounded-lg hover:bg-[#F15A29] focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    {fetchLoading ? (
                      <>
                        <Loader className="h-5 w-5 animate-spin mr-2" />
                        Signing up...
                      </>
                    ) : (
                      "Sign Up"
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>

          {/* Right Column: Help Section */}
          <div className="w-1/3 flex flex-col justify-center bg-[#FBFAF9] p-12">
            <div className="text-center">
              <img
                src={UserSignInIcon}
                alt="Help Illustration"
                className="mx-auto mb-16"
              />
              <div className="flex flex-col gap-10 justify-center">
                <p className="text-center w-full text-[#919191] nunito-regular text-text">
                  Tell us more about yourself!
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
}
